import React, { useState, useRef, useEffect } from 'react';
import { Icon, Popup, Label, Header, Image } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Avatar from 'react-avatar';
import { signout } from '../../actions';
import { Languager } from '../../components';
import { Theme } from '../../res';
import PATHS from '../../Routers/PATHS';
import avatarImage from '../../res/images/avatar.svg';

const AdminNavBar = ({ user, onMenuToggle, isMenuOpen }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const { notImportantSeen } = useSelector(({ notifications }) => notifications);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    const handleSignout = () => {
        dispatch(signout());
    };

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the click is outside the menu container
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                // Also check if click is on the Semantic UI Popup portal (language menu)
                const popupElement = document.querySelector('.ui.popup.visible');
                const isInLanguagePopup = popupElement && popupElement.contains(event.target);
                
                if (!isInLanguagePopup) {
                    setIsUserMenuOpen(false);
                }
            }
        };

        if (isUserMenuOpen) {
            // Use a small delay to allow link clicks to process first
            const timeoutId = setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [isUserMenuOpen]);

    if (!user) return null;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            backgroundColor: Theme.colors.WHITE,
            borderBottom: `1px solid ${Theme.colors.GREY_10}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 999
        }}>
            {/* Left Side: Menu Toggle and Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                <Icon
                    link
                    name='bars'
                    size='large'
                    onClick={onMenuToggle}
                    style={{
                        color: Theme.colors.PRIMARY_50,
                        cursor: 'pointer'
                    }}
                />
                <Link to={PATHS.ADMIN_DASHBOARD.URL} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                    <Image 
                        src="/images/logo.svg"
                        style={{ 
                            height: '50px', 
                            width: 'auto',
                            maxWidth: '200px',
                            objectFit: 'contain'
                        }} 
                    />
                </Link>
            </div>

            {/* Right Side: Notifications and User */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexDirection: isArabic ? 'row-reverse' : 'row'
            }}>
                {/* Notification Button - Navigate to Notifications Page */}
                <Link 
                    to={PATHS.USER_NOTIFICATIONS.URL} 
                    style={{ 
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        color: 'inherit'
                    }}
                >
                    <Icon
                        name={notImportantSeen > 0 ? 'bell' : 'bell outline'}
                        color={notImportantSeen > 0 ? 'red' : 'grey'}
                        size='large'
                    />
                    {notImportantSeen > 0 && (
                        <Label
                            color='red'
                            circular
                            size='tiny'
                            style={{
                                position: 'absolute',
                                top: '-5px',
                                [isArabic ? 'left' : 'right']: '-5px',
                                minWidth: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0
                            }}
                        >
                            {notImportantSeen}
                        </Label>
                    )}
                </Link>

                {/* Current User - Clickable Menu */}
                <div ref={userMenuRef} style={{ position: 'relative' }}>
                    <Popup
                        open={isUserMenuOpen}
                        onClose={() => setIsUserMenuOpen(false)}
                        on='click'
                        flowing
                        hoverable={false}
                        position={isArabic ? 'bottom left' : 'bottom right'}
                        closeOnDocumentClick={false}
                        closeOnTriggerClick={false}
                        trigger={
                            <div 
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flexDirection: isArabic ? 'row-reverse' : 'row',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Theme.colors.GREY_05}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <Avatar
                                    src={user.coverImgUrl || avatarImage}
                                    round
                                    size='40'
                                />
                                <Header as='h5' style={{ margin: 0 }}>
                                    <Header.Content style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: Theme.colors.GREY_90
                                    }}>
                                        {`${user.firstName} ${user.lastName || ''}`.trim()}
                                    </Header.Content>
                                </Header>
                            </div>
                        }
                    >
                        <div style={{ padding: '4px', minWidth: '180px' }}>
                        {/* Profile Button */}
                        <Link
                            to={PATHS.LANDING_PROFILE.URL}
                            onClick={() => setIsUserMenuOpen(false)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                color: Theme.colors.GREY_90,
                                textDecoration: 'none',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Theme.colors.GREY_05}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <Icon name='user' />
                            <span style={{ fontWeight: 500 }}>{t('Profile')}</span>
                        </Link>

                        {/* Language Button */}
                        <Popup
                            on='click'
                            flowing
                            hoverable
                            position={isArabic ? 'left center' : 'right center'}
                            closeOnDocumentClick={true}
                            trigger={
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 12px',
                                        borderRadius: '4px',
                                        color: Theme.colors.GREY_90,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Theme.colors.GREY_05}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <Icon name='globe' />
                                    <span style={{ fontWeight: 500 }}>{t('Language')}</span>
                                </div>
                            }
                        >
                            <Languager isMenu />
                        </Popup>

                        {/* Divider */}
                        <div style={{
                            height: '1px',
                            backgroundColor: Theme.colors.GREY_10,
                            margin: '4px 0'
                        }} />

                        {/* Logout Button */}
                        <div
                            onClick={handleSignout}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                color: Theme.colors.ERROR_75,
                                fontWeight: 500,
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Theme.colors.ERROR_25}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <Icon name='sign out' />
                            <span>{t('Logout')}</span>
                        </div>
                    </div>
                    </Popup>
                </div>
            </div>
        </div>
    );
};

export default AdminNavBar;

